import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { publicAssetUrl } from "@/lib/api";
import { PostStatusBadge, PostTypePill } from "@/components/shared/Badges";
import { MapPin, Tag, Zap, TicketPercent } from "lucide-react";
import { HeartButton } from "@/components/customer/HeartButton";
import { haversineKm, formatDistance } from "@/lib/distance";
import { CountdownStrip, EndingSoonBadge } from "@/components/posts/CountdownStrip";

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
};

/** Days-remaining badge — grey / amber / red pulsing. Returns null when expired or unknown. */
function DaysRemainingBadge({ validTo }) {
    if (!validTo) return null;
    const ms = new Date(validTo).getTime() - Date.now();
    if (ms <= 0) return null;
    const days = Math.ceil(ms / 86400000);
    let cls = "bg-neutral-100 text-neutral-600";
    let pulse = "";
    if (days <= 3) { cls = "bg-red-100 text-red-700"; pulse = "animate-pulse"; }
    else if (days <= 7) { cls = "bg-amber-100 text-amber-800"; }
    const label = days === 1 ? "1 day left" : `${days} days left`;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${cls} ${pulse}`} data-testid="days-remaining-badge">
            <span aria-hidden>⏳</span> {label}
        </span>
    );
}

/** Small round shop logo, bottom-left of the poster. Uses initials fallback. */
function ShopLogoOverlay({ logoUrl, businessName }) {
    const initials = (businessName || "?").split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("");
    return (
        <div className="absolute bottom-2 left-2 flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-orange-500 text-[11px] font-black text-white shadow" data-testid="shop-logo-overlay">
            {logoUrl ? (
                <img src={publicAssetUrl(logoUrl)} alt={businessName || "shop"} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
                <span>{initials}</span>
            )}
        </div>
    );
}

export const PostCard = ({ post, showStatus = false, userLat = null, userLng = null }) => {
    const discount = post?.details?.discount_percent;
    const isFlash = !!post.boosted;
    const couponOn = !!post?.details?.coupon_enabled;
    const nav = useNavigate();

    // Distance — prefer backend-computed (nearby query) else compute client-side.
    let distanceKm = post.distance_km;
    if (distanceKm == null && userLat != null && userLng != null && post.business_lat != null && post.business_lng != null) {
        distanceKm = haversineKm(userLat, userLng, post.business_lat, post.business_lng);
    }
    const showDistance = distanceKm != null;

    const goDetail = (e) => { e.preventDefault(); e.stopPropagation(); nav(`/posts/${post.id}?claim=1`); };

    return (
        <Link
            to={`/posts/${post.id}`}
            data-testid={`post-card-${post.id}`}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition hover:-translate-y-0.5 hover:shadow-xl"
        >
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-neutral-100">
                {post.poster_image_url ? (
                    <img
                        src={publicAssetUrl(post.poster_image_url)}
                        alt={post.title}
                        className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center nest-gradient text-white">
                        <div className="text-2xl font-black tracking-tight">OfferNest</div>
                    </div>
                )}

                {/* Live HH:MM:SS countdown strip (bottom overlay) */}
                {post.valid_to && post.type === "OFFER" && (
                    <CountdownStrip validTo={post.valid_to} size="md" testid={`countdown-${post.id}`} />
                )}

                {/* Discount pill */}
                {typeof discount === "number" && post.type === "OFFER" && (
                    <div className="absolute left-3 top-11 rounded-full bg-white/95 px-3 py-1 text-sm font-bold text-orange-600 shadow" data-testid={`discount-pill-${post.id}`}>
                        {discount}% OFF
                    </div>
                )}

                {/* Top-right: type + status + heart + distance + ending-soon */}
                <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                    {post.type === "OFFER" && (
                        <EndingSoonBadge validTo={post.valid_to} testid={`ending-soon-${post.id}`} />
                    )}
                    <PostTypePill type={post.type} />
                    {showStatus ? <PostStatusBadge status={post.status} /> : null}
                    <HeartButton post={post} className="mt-1" />
                    {showDistance && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-bold text-neutral-800 shadow" data-testid="distance-badge">
                            <MapPin size={10} /> {formatDistance(distanceKm)}
                        </span>
                    )}
                </div>

                {/* Bottom-left: shop logo overlay */}
                <ShopLogoOverlay logoUrl={post.shop_logo_url} businessName={post.business_name} />

                {/* Sponsored / boosted / partner chips (keep to bottom-right to avoid overlap with logo) */}
                {(post.sponsored || post.boosted) && (
                    <div className="absolute right-3 bottom-3">
                        {post.sponsored ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur" data-testid={`post-sponsored-chip-${post.id}`}>
                                ★ Sponsored
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow" data-testid={`post-boosted-chip-${post.id}`}>
                                <Zap size={10} /> Boosted
                            </span>
                        )}
                    </div>
                )}
                {!post.sponsored && !post.boosted && post.details?.partner_brand_slug && (
                    <div className="absolute right-3 bottom-3">
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-800 shadow" data-testid={`post-partner-chip-${post.id}`}>
                            via {post.details.partner_brand_slug}
                        </span>
                    </div>
                )}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-4">
                <div className="text-base font-semibold leading-tight text-neutral-900 line-clamp-2">
                    {post.title}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <DaysRemainingBadge validTo={post.valid_to} />
                    {couponOn && (
                        <button
                            type="button"
                            onClick={goDetail}
                            data-testid={`claim-chip-${post.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-0.5 text-[10px] font-bold text-white shadow transition hover:bg-orange-600"
                        >
                            <TicketPercent size={11} /> Claim
                        </button>
                    )}
                </div>
                {post.business_name ? (
                    <div className="text-xs font-medium text-neutral-500">{post.business_name}</div>
                ) : null}
                <div className="mt-auto flex flex-wrap items-center gap-3 pt-2 text-xs text-neutral-600">
                    <span className="inline-flex items-center gap-1">
                        <MapPin size={13} /> <span className="capitalize">{post.district_slug}</span>
                    </span>
                    {post.category_slug ? (
                        <span className="inline-flex items-center gap-1">
                            <Tag size={13} /> <span className="capitalize">{post.category_slug.replace(/-/g, " ")}</span>
                        </span>
                    ) : null}
                    {post.valid_to ? (
                        <span className="text-neutral-500">till {fmtDate(post.valid_to)}</span>
                    ) : null}
                </div>
            </div>
        </Link>
    );
};

export default PostCard;
